/* Reuse bundled Swiper 11. No cloned videos, global scroll handlers or RAF loop. */
(() => {
  'use strict';
  if (customElements.get('lukzer-reels')) return;
  class LukzerReels extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      if (document.readyState === 'loading') {
        this.ready = () => { if (this.isConnected) this.initialize(); };
        document.addEventListener('DOMContentLoaded', this.ready, { once: true });
      } else this.initialize();
    }
    initialize() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.viewport = this.querySelector('[data-lkz-reels-viewport]');
      this.cards = [...this.querySelectorAll('.lkz-reel-card')];
      this.videos = [...this.querySelectorAll('[data-lkz-reel-video]')];
      this.progress = this.querySelector('[role="progressbar"]');
      this.previous = this.querySelector('[data-reels-prev]');
      this.next = this.querySelector('[data-reels-next]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.delay = Math.min(5000, Math.max(4500, Number(this.dataset.autoplayDelay) || 4750));
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      this.interacting = false;
      this.playingVideo = null;
      this.visibleCards = new Set();
      this.cards.forEach((card, index) => { card.dataset.reelIndex = index; });
      this.viewport.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.videos.forEach(video => {
        video.addEventListener('playing', () => {
          if (video !== this.playingVideo || !this.canPlay()) { video.pause(); return; }
          this.videos.forEach(other => { if (other !== video) other.pause(); });
          video.classList.add('is-playing');
        }, { signal });
        video.addEventListener('error', () => video.classList.remove('is-playing'), { signal });
      });
      if (typeof window.Swiper !== 'function' || !this.cards.length) return;
      this.slider = new window.Swiper(this.viewport, {
        slidesPerView: Number(this.dataset.mobile) || 1.18,
        slidesPerGroup: 1,
        spaceBetween: this.gap(),
        speed: this.motion.matches ? 0 : 600,
        loop: this.cards.length >= Math.ceil(Number(this.dataset.desktop) || 4.25) + 1,
        loopAddBlankSlides: false,
        rewind: false,
        watchOverflow: true,
        simulateTouch: true,
        focusableElements: 'input, select, option, textarea, button, label',
        threshold: 8,
        touchAngle: 35,
        touchStartPreventDefault: false,
        preventClicks: true,
        preventClicksPropagation: true,
        mousewheel: false,
        autoplay: false,
        a11y: { scrollOnFocus: false, slideLabelMessage: 'Reel {{index}} of {{slidesLength}}' },
        breakpoints: { 768: { slidesPerView: Number(this.dataset.tablet) || 2.4 }, 1200: { slidesPerView: Number(this.dataset.desktop) || 4.25 } },
        on: {
          slideChange: () => { this.pauseMedia(); this.updateProgress(); },
          transitionStart: () => { this.querySelector('[data-lkz-reels-track]').style.willChange = 'transform'; this.pauseMedia(); },
          transitionEnd: () => { this.querySelector('[data-lkz-reels-track]').style.willChange = ''; this.updateControls(); this.syncMedia(); },
          touchStart: () => { this.interacting = true; this.stopTimer(); this.pauseMedia(); },
          touchEnd: () => { this.interacting = false; this.schedule(6500); this.syncMedia(); },
          resize: () => { this.updateControls(); this.syncMedia(); }
        }
      });
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.stopTimer();
      }, { signal });
      this.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.schedule();
      }, { signal });
      this.addEventListener('focusin', event => {
        this.focused = true;
        this.stopTimer();
        const card = event.target.closest('.lkz-reel-card');
        if (card && !this.visibleCards.has(card)) {
          const index = Number(card.dataset.reelIndex);
          if (this.slider.params.loop) this.slider.slideToLoop(index);
          else this.slider.slideTo(index);
        }
      }, { signal });
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        if (!this.focused) this.schedule();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        this.stopTimer();
        this.syncMedia();
        this.schedule();
      }, { signal });
      this.motionChange = () => {
        this.slider.params.speed = this.motion.matches ? 0 : 600;
        this.stopTimer();
        this.syncMedia();
        this.schedule();
      };
      this.motion.addEventListener('change', this.motionChange);
      this.sectionObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.stopTimer();
        this.syncMedia();
        this.schedule();
      }, { threshold: 0 });
      this.sectionObserver.observe(this.viewport);
      this.mediaObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= .55) this.visibleCards.add(entry.target);
          else this.visibleCards.delete(entry.target);
        });
        this.syncMedia();
      }, { threshold: [0, .55] });
      this.cards.forEach(card => this.mediaObserver.observe(card));
      this.updateProgress();
      this.updateControls();
    }
    gap() { return parseFloat(getComputedStyle(this).getPropertyValue('--lkz-reel-gap')) || 20; }
    canPlay() { return this.isConnected && this.visible && !document.hidden && !this.motion.matches && !this.interacting && !this.slider?.animating; }
    pauseMedia() {
      this.playingVideo = null;
      this.videos?.forEach(video => { video.pause(); video.classList.remove('is-playing'); });
    }
    syncMedia() {
      if (!this.slider || !this.canPlay()) { this.pauseMedia(); return; }
      const card = this.slider.slides[this.slider.activeIndex];
      const video = card && this.visibleCards.has(card) && card.querySelector('video');
      if (!video) { this.pauseMedia(); return; }
      if (video === this.playingVideo) return;
      this.pauseMedia();
      this.playingVideo = video;
      if (!video.getAttribute('src')) video.src = video.dataset.src;
      video.muted = true;
      video.play().catch(() => { if (this.playingVideo === video) video.classList.remove('is-playing'); });
    }
    stopTimer() { clearTimeout(this.timer); this.timer = null; }
    schedule(delay = this.delay) {
      this.stopTimer();
      if (!this.slider || this.dataset.autoplay !== 'true' || !this.visible || document.hidden || this.motion.matches || this.hovered || this.focused || this.interacting || this.slider.isLocked) return;
      if (!this.slider.params.loop && this.slider.isEnd) return;
      this.timer = setTimeout(() => {
        if (!this.slider.animating) this.slider.slideNext();
        this.schedule();
      }, delay);
    }
    move(direction) {
      this.stopTimer();
      if (!this.slider || this.slider.animating) return;
      direction > 0 ? this.slider.slideNext() : this.slider.slidePrev();
      this.schedule(6500);
    }
    updateProgress() {
      if (!this.progress || !this.slider) return;
      const current = this.slider.realIndex + 1;
      this.progress.setAttribute('aria-valuemax', this.cards.length);
      this.progress.setAttribute('aria-valuenow', current);
      this.progress.style.setProperty('--lkz-reel-progress', current / this.cards.length);
    }
    updateControls() {
      if (!this.slider) return;
      this.previous.disabled = this.slider.isLocked || (!this.slider.params.loop && this.slider.isBeginning);
      this.next.disabled = this.slider.isLocked || (!this.slider.params.loop && this.slider.isEnd);
    }
    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.ready);
      this.stopTimer();
      this.pauseMedia();
      this.sectionObserver?.disconnect();
      this.mediaObserver?.disconnect();
      this.motion?.removeEventListener('change', this.motionChange);
      this.controller?.abort();
      this.slider?.destroy(true, true);
      this.slider = null;
      this.controller = null;
    }
  }
  customElements.define('lukzer-reels', LukzerReels);
})();
